import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceChecklist = ({ checklistItems, onUpdateItem }) => {
  const [selectedRole, setSelectedRole] = useState('all');
  const [expandedSections, setExpandedSections] = useState(new Set(['legal']));

  const roles = [
    { id: 'all', label: 'All Requirements', count: checklistItems?.length },
    { id: 'legal', label: 'Legal', count: checklistItems?.filter(item => item?.role === 'legal')?.length },
    { id: 'financial', label: 'Financial', count: checklistItems?.filter(item => item?.role === 'financial')?.length },
    { id: 'technical', label: 'Technical', count: checklistItems?.filter(item => item?.role === 'technical')?.length },
    { id: 'compliance', label: 'Compliance', count: checklistItems?.filter(item => item?.role === 'compliance')?.length }
  ];

  const filteredItems = selectedRole === 'all' 
    ? checklistItems 
    : checklistItems?.filter(item => item?.role === selectedRole);

  const groupedItems = filteredItems?.reduce((acc, item) => {
    if (!acc?.[item?.category]) {
      acc[item.category] = [];
    }
    acc?.[item?.category]?.push(item);
    return acc;
  }, {});

  const toggleSection = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(section)) {
      newExpanded?.delete(section);
    } else {
      newExpanded?.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in-progress': return 'text-warning bg-warning/10';
      case 'pending': return 'text-muted-foreground bg-muted';
      case 'overdue': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'pending': return 'Circle';
      case 'overdue': return 'AlertTriangle';
      default: return 'Circle';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const calculateCategoryProgress = (items) => {
    const completed = items?.filter(item => item?.status === 'completed')?.length;
    return Math.round((completed / items?.length) * 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Compliance Checklist</h3>
        <Button variant="outline" iconName="Download" iconPosition="left">
          Export Report
        </Button>
      </div>
      {/* Role Filter */}
      <div className="flex space-x-2 overflow-x-auto">
        {roles?.map((role) => (
          <button
            key={role?.id}
            onClick={() => setSelectedRole(role?.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-smooth ${
              selectedRole === role?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <span>{role?.label}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${
              selectedRole === role?.id
                ? 'bg-primary-foreground/20 text-primary-foreground'
                : 'bg-background text-muted-foreground'
            }`}>
              {role?.count}
            </span>
          </button>
        ))}
      </div>
      {/* Checklist Sections */}
      <div className="space-y-4">
        {Object.entries(groupedItems)?.map(([category, items]) => {
          const progress = calculateCategoryProgress(items);
          const isExpanded = expandedSections?.has(category);
          
          return (
            <div key={category} className="bg-card border border-border rounded-lg">
              <button
                onClick={() => toggleSection(category)}
                className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-smooth"
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
                    size={16} 
                    className="text-muted-foreground" 
                  />
                  <h4 className="font-medium text-foreground capitalize">{category?.replace('-', ' ')}</h4>
                  <span className="text-sm text-muted-foreground">({items?.length} items)</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          progress === 100 ? 'bg-success' : 
                          progress >= 50 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground">{progress}%</span>
                  </div>
                </div>
              </button>
              {isExpanded && (
                <div className="border-t border-border">
                  <div className="p-4 space-y-3">
                    {items?.map((item) => (
                      <div key={item?.id} className="flex items-start space-x-3 p-3 bg-muted/20 rounded-lg">
                        <button
                          onClick={() => onUpdateItem(item?.id, { 
                            status: item?.status === 'completed' ? 'pending' : 'completed' 
                          })}
                          className="flex-shrink-0 mt-0.5"
                        >
                          <Icon 
                            name={getStatusIcon(item?.status)} 
                            size={20} 
                            className={`transition-smooth ${
                              item?.status === 'completed' ? 'text-success' : 'text-muted-foreground hover:text-accent'
                            }`}
                          />
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <h5 className={`font-medium ${
                              item?.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground'
                            }`}>
                              {item?.title}
                            </h5>
                            
                            <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item?.status)}`}>
                                {item?.status?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                              </div>
                              
                              {item?.priority && (
                                <Icon 
                                  name="Flag" 
                                  size={14} 
                                  className={getPriorityColor(item?.priority)}
                                  title={`${item?.priority} priority`}
                                />
                              )}
                            </div>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-3">{item?.description}</p>
                          
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <div className="flex items-center space-x-4">
                              <span>Assigned to: {item?.assignedTo}</span>
                              {item?.dueDate && (
                                <>
                                  <span>•</span>
                                  <span className={item?.status === 'overdue' ? 'text-error' : ''}>
                                    Due: {item?.dueDate}
                                  </span>
                                </>
                              )}
                            </div>
                            
                            {item?.relatedDocuments && item?.relatedDocuments?.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <Icon name="FileText" size={12} />
                                <span>{item?.relatedDocuments?.length} docs</span>
                              </div>
                            )}
                          </div>
                          
                          {item?.notes && (
                            <div className="mt-2 p-2 bg-background rounded text-xs">
                              <Icon name="MessageSquare" size={12} className="inline mr-1" />
                              {item?.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-success">
            {filteredItems?.filter(item => item?.status === 'completed')?.length}
          </p>
          <p className="text-sm text-muted-foreground">Completed</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-warning">
            {filteredItems?.filter(item => item?.status === 'in-progress')?.length}
          </p>
          <p className="text-sm text-muted-foreground">In Progress</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-error">
            {filteredItems?.filter(item => item?.status === 'overdue')?.length}
          </p>
          <p className="text-sm text-muted-foreground">Overdue</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-foreground">
            {filteredItems?.length}
          </p>
          <p className="text-sm text-muted-foreground">Total</p>
        </div>
      </div>
    </div>
  );
};

export default ComplianceChecklist;